import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Sidebar } from "../includes/sidebar";
import { Topbar } from "../includes/topbar";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "../../components/ui/dropdown-menu";
import { MoreHorizontal, Plus, Search, Eye, UserCheck, UserX, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, QrCode, Download, Pencil, RefreshCw, Loader2, Copy, Mail } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Separator } from "../../components/ui/separator";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import { agentsApi, Agent, AgentConfig, CreateAgentRequest } from "../../api/agents.api";
import { comptesApi } from "../../api/clients.api";
import { PhoneInput } from "../../components/ui/phone-input";
import { useToast } from "../../hooks/use-toast";
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";

function CreateAgentDialog({ onSuccess }: { onSuccess: () => void }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        nom: "",
        prenom: "",
        surnom: "",
        email: "",
        telephone: "",
        sexe: "",
        dateNaissance: "",
        adresse: "",
        ville: "",
        type: "Agent" as const,
        devise: "HTG",
    });
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const { toast } = useToast();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        // Clear error when user types
        if (fieldErrors[name] || fieldErrors.general) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                delete newErrors.general;
                return newErrors;
            });
        }
    };

    const handleSelectChange = (name: string, value: string) => {
        setFormData({ ...formData, [name]: value });
        // Clear error on select change
        if (fieldErrors[name] || fieldErrors.general) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                delete newErrors.general;
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setFieldErrors({});

        try {
            await agentsApi.create(formData);
            toast({ title: t('common:success'), description: t('accounts:agents.dialogs.create.success'), type: "success" });
            setOpen(false);
            setFormData({
                nom: "",
                prenom: "",
                surnom: "",
                email: "",
                telephone: "",
                sexe: "",
                dateNaissance: "",
                adresse: "",
                ville: "",
                type: "Agent",
                devise: "HTG",
            }); onSuccess();
        } catch (error: any) {
            // Error handling logic similar to CreateClientModal
            const message = error.response?.data?.message || t('accounts:agents.dialogs.create.error');
            const msgLower = message.toLowerCase();

            if (msgLower.includes('email') || msgLower.includes('imel') || msgLower.includes('imèl')) {
                setFieldErrors({ email: message });
            } else if (msgLower.includes('telefòn') || msgLower.includes('telephone') || msgLower.includes('nimewo')) {
                setFieldErrors({ telephone: message });
            } else {
                setFieldErrors({ general: message });
                toast({ title: t('common:error'), description: message, type: "error" });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm">
                    <Plus className="mr-2 h-4 w-4" /> {t('accounts:agents.actions.add')}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] w-[95vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{t('accounts:agents.dialogs.create.title')}</DialogTitle>
                    <DialogDescription>
                        {t('accounts:agents.dialogs.create.description')}
                    </DialogDescription>
                </DialogHeader>
                {fieldErrors.general && (
                    <div className="p-3 mb-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
                        {fieldErrors.general}
                    </div>
                )}
                <form onSubmit={handleSubmit} className="grid gap-4 py-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="prenom">{t('common:labels.firstName')}</Label>
                            <Input id="prenom" name="prenom" value={formData.prenom} onChange={handleChange} required />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="nom">{t('common:labels.lastName')}</Label>
                            <Input id="nom" name="nom" value={formData.nom} onChange={handleChange} required />
                        </div>
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="email">{t('common:labels.email')}</Label>
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className={fieldErrors.email ? "border-red-500" : ""}
                        />
                        {fieldErrors.email && <p className="text-xs text-red-500">{fieldErrors.email}</p>}
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="telephone">{t('common:labels.phone')}</Label>
                        <PhoneInput
                            id="telephone"
                            placeholder="Ex: 509..."
                            value={formData.telephone}
                            onChange={(val: string | undefined) => {
                                setFormData({ ...formData, telephone: val || "" });
                                if (fieldErrors.telephone) {
                                    setFieldErrors(prev => {
                                        const newErrors = { ...prev };
                                        delete newErrors.telephone;
                                        return newErrors;
                                    });
                                }
                            }}
                            defaultCountry="HT"
                            className={fieldErrors.telephone ? "border-red-500" : ""}
                        />
                        {fieldErrors.telephone && <p className="text-xs text-red-500">{fieldErrors.telephone}</p>}
                    </div>
                    {/* Extended Fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="surnom">{t('common:labels.nickname')}</Label>
                            <Input id="surnom" name="surnom" value={formData.surnom} onChange={handleChange} />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="sexe">{t('common:labels.gender')}</Label>
                            <Select value={formData.sexe} onValueChange={(v) => handleSelectChange("sexe", v)}>
                                <SelectTrigger>
                                    <SelectValue placeholder={t('common:labels.choose')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="M">{t('common:labels.male')}</SelectItem>
                                    <SelectItem value="F">{t('common:labels.female')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="dateNaissance">{t('common:labels.birthDate')}</Label>
                            <Input id="dateNaissance" name="dateNaissance" type="date" value={formData.dateNaissance} onChange={handleChange} />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="adresse">{t('common:labels.address')}</Label>
                            <Input id="adresse" name="adresse" value={formData.adresse} onChange={handleChange} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="type">{t('common:labels.type')}</Label>
                            <Select value={formData.type} onValueChange={(v) => handleSelectChange("type", v)}>
                                <SelectTrigger>
                                    <SelectValue placeholder={t('common:labels.choose')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Agent">Agent</SelectItem>
                                    <SelectItem value="SuperAgent">Super Agent</SelectItem>
                                    <SelectItem value="Marchant">Marchand</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="devise">{t('common:labels.currency')}</Label>
                            <Select value={formData.devise} onValueChange={(v) => handleSelectChange("devise", v)}>
                                <SelectTrigger>
                                    <SelectValue placeholder="HTG" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="HTG">HTG (Goud)</SelectItem>
                                    <SelectItem value="USD">USD (Dola)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="flex justify-end pt-4">
                        <Button type="submit" disabled={isLoading}>
                            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {t('accounts:agents.actions.add')}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// Agent QR Code Dialog Component
function AgentQRDialog({ agent }: { agent: Agent }) {
    const { t } = useTranslation();
    const qrRef = useRef<HTMLCanvasElement>(null);
    const { toast } = useToast();

    const handleDownloadQR = () => {
        if (!qrRef.current) return;

        try {
            const canvas = qrRef.current;
            const url = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.download = `qr-agent-${agent.code}-${agent.client?.prenom || 'agent'}.png`;
            link.href = url;
            link.click();

            toast({
                title: t('common:success'),
                description: t('accounts:agents.qr.success'),
                type: "success",
            });
        } catch (error) {
            toast({
                title: t('common:error'),
                description: t('accounts:agents.qr.error'),
                type: "error",
            });
        }
    };

    const handleCopyCode = () => {
        navigator.clipboard.writeText(agent.code).then(() => {
            toast({
                title: t('common:success'),
                description: t('accounts:agents.actions.copyConfirm', { code: agent.code }),
                type: "success",
            });
        }).catch(() => {
            toast({
                title: t('common:error'),
                description: t('common:error'),
                type: "error",
            });
        });
    };

    return (
        <Dialog>
            <DialogTrigger asChild>
                <div className="flex h-8 w-8 items-center justify-center rounded-md border bg-muted hover:bg-muted/80 cursor-pointer group transition-colors">
                    <QrCode className="h-4 w-4 group-hover:hidden" />
                    <Eye className="h-4 w-4 hidden group-hover:block" />
                </div>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('accounts:agents.qr.title')} - {agent.client?.prenom} {agent.client?.nom}</DialogTitle>
                    <DialogDescription>
                        {t('accounts:agents.table.code')}: <span className="font-mono font-semibold">{agent.code}</span>
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col items-center justify-center py-6 gap-4">
                    {/* Real QR Code with Agent Code */}
                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                        <QRCodeCanvas
                            ref={qrRef}
                            value={agent.code}
                            size={200}
                            level="H"
                            includeMargin={true}
                        />
                    </div>

                    {/* Agent Code Display */}
                    <div className="w-full px-4">
                        <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                            <div className="flex-1 text-center">
                                <p className="text-xs text-muted-foreground mb-1">{t('accounts:agents.table.code')}</p>
                                <code className="text-lg font-bold font-mono">{agent.code}</code>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={handleCopyCode}
                                title={t('accounts:agents.actions.copyCode')}
                            >
                                <Copy className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    {/* Agent Info */}
                    <div className="w-full px-4 space-y-1 text-sm text-muted-foreground">
                        <p><span className="font-medium">{t('common:labels.type')}:</span> {agent.type}</p>
                        <p><span className="font-medium">{t('common:labels.phone')}:</span> {agent.client?.telephone || 'N/A'}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2"
                            onClick={handleDownloadQR}
                        >
                            <Download className="h-4 w-4" />
                            {t('accounts:agents.qr.download')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function AgentsPage() {
    const { t } = useTranslation();
    const [agents, setAgents] = useState<Agent[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [typeFilter, setTypeFilter] = useState("Tous");
    const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
    const [showProfileDialog, setShowProfileDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const { toast } = useToast();

    // Agent config
    const [agentConfig, setAgentConfig] = useState<AgentConfig | null>(null);
    const [isUpdatingConfig, setIsUpdatingConfig] = useState(false);

    // Agent clients tab
    const [activeAgentTab, setActiveAgentTab] = useState<'overview' | 'clients'>('overview');
    const [agentClients, setAgentClients] = useState<any[]>([]);
    const [isLoadingAgentClients, setIsLoadingAgentClients] = useState(false);
    const [agentClientsTotal, setAgentClientsTotal] = useState(0);
    const [agentClientsPage, setAgentClientsPage] = useState(1);
    const [agentClientsSearch, setAgentClientsSearch] = useState('');

    const fetchAgents = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await agentsApi.getAll();
            setAgents(data.agents || []);
        } catch (error) {
            console.error("Failed to fetch agents", error);
            toast({
                title: t('common:error'),
                description: t('accounts:agents.table.error'),
                type: "error",
            });
        } finally {
            setIsLoading(false);
        }
    }, [t, toast]);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    useEffect(() => {
        if (!selectedAgent?.id || !showProfileDialog) return;
        setActiveAgentTab('overview');
        setAgentClientsPage(1);
        setAgentClientsSearch('');
        setAgentConfig(null);
        agentsApi.getConfig(selectedAgent.id)
            .then(cfg => setAgentConfig(cfg))
            .catch(() => {});
    }, [selectedAgent?.id, showProfileDialog]);

    useEffect(() => {
        if (!selectedAgent?.id || activeAgentTab !== 'clients') return;
        setIsLoadingAgentClients(true);
        comptesApi.getAgentClients(selectedAgent.id, agentClientsPage, agentClientsSearch)
            .then((res: { comptes: any[]; total: number }) => {
                setAgentClients(res.comptes || []);
                setAgentClientsTotal(res.total || 0);
            })
            .catch(() => {})
            .finally(() => setIsLoadingAgentClients(false));
    }, [selectedAgent?.id, activeAgentTab, agentClientsPage, agentClientsSearch]);

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [agentToToggle, setAgentToToggle] = useState<Agent | null>(null);

    const handleToggleWithdrawDirect = async () => {
        if (!selectedAgent || !agentConfig) return;
        const newVal = !agentConfig.canWithdrawDirect;
        setIsUpdatingConfig(true);
        try {
            await agentsApi.updateConfig(selectedAgent.id, newVal, agentConfig.clientAccessMode);
            // Re-fetch config fresh — garanti UI reflète eta DB a
            const fresh = await agentsApi.getConfig(selectedAgent.id);
            setAgentConfig(fresh);
            toast({
                title: t('common:success'),
                description: newVal ? 'Ajan ka fè retrè dirèkteman' : 'Retrè ajan bezwen apwobasyon admin',
                type: 'success',
            });
        } catch {
            toast({ title: t('common:error'), description: 'Pa kapab mete ajou konfigirasyon', type: 'error' });
        } finally {
            setIsUpdatingConfig(false);
        }
    };

    const handleToggleAccessMode = async () => {
        if (!selectedAgent || !agentConfig) return;
        const newMode: 'full' | 'assigned_only' =
            agentConfig.clientAccessMode === 'assigned_only' ? 'full' : 'assigned_only';
        setIsUpdatingConfig(true);
        try {
            await agentsApi.updateConfig(selectedAgent.id, agentConfig.canWithdrawDirect, newMode);
            // Re-fetch config fresh — evite problem serialization TypeORM nan PUT response
            const fresh = await agentsApi.getConfig(selectedAgent.id);
            setAgentConfig(fresh);
            toast({
                title: t('common:success'),
                description: newMode === 'assigned_only'
                    ? 'Ajan wè sèlman kliyan ki asiye ba li'
                    : 'Ajan ka wè tout kliyan',
                type: 'success',
            });
        } catch {
            toast({ title: t('common:error'), description: 'Pa kapab mete ajou konfigirasyon', type: 'error' });
        } finally {
            setIsUpdatingConfig(false);
        }
    };

    const handleToggleStatus = (agent: Agent) => {
        setAgentToToggle(agent);
        setShowConfirmDialog(true);
    };

    const confirmToggleStatus = async () => {
        if (!agentToToggle) return;

        try {
            if (agentToToggle.actif) {
                await agentsApi.deactivate(agentToToggle.id);
                toast({ title: t('common:success'), description: t('accounts:agents.actions.deactivate'), type: "success" });
            } else {
                await agentsApi.activate(agentToToggle.id);
                toast({ title: t('common:success'), description: t('accounts:agents.actions.activate'), type: "success" });
            }
            fetchAgents(); // Refresh list
        } catch (error) {
            toast({ title: t('common:error'), description: t('common:error'), type: "error" });
        } finally {
            setShowConfirmDialog(false);
            setAgentToToggle(null);
        }
    };

    const handleViewProfile = (agent: Agent) => {
        setSelectedAgent(agent);
        setShowProfileDialog(true);
    };

    const handleEdit = (agent: Agent) => {
        setSelectedAgent(agent);
        setShowEditDialog(true);
    };

    const filteredAgents = agents.filter(agent => {
        const clientName = agent.client?.nom || "";
        const clientPrenom = agent.client?.prenom || "";
        const fullName = `${clientName} ${clientPrenom}`.toLowerCase();

        const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
            agent.code.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = typeFilter === "Tous" || agent.type === typeFilter;
        return matchesSearch && matchesType;
    });

    const totalPages = Math.ceil(filteredAgents.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentAgents = filteredAgents.slice(indexOfFirstItem, indexOfLastItem);

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto space-y-4 p-8 pt-6">
                    <div className="flex items-center justify-between space-y-2">
                        <Breadcrumb>
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink className="capitalize">{t('dashboard:sidebar.sections.comptes')}</BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbPage className="capitalize">{t('dashboard:sidebar.items.agents')}</BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>
                    </div>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <div className="space-y-1">
                                <CardTitle>{t('accounts:agents.title')}</CardTitle>
                                <CardDescription>
                                    {t('accounts:agents.description')}
                                </CardDescription>
                            </div>
                            <CreateAgentDialog onSuccess={fetchAgents} />
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center py-4 space-x-2">
                                <div className="relative w-full max-w-sm">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder={t('accounts:agents.searchPlaceholder')}
                                        className="pl-8"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <Select value={typeFilter} onValueChange={setTypeFilter}>
                                    <SelectTrigger className="w-[180px]">
                                        <SelectValue placeholder={t('common:labels.type')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Tous">{t('accounts:agents.filters.allTypes')}</SelectItem>
                                        <SelectItem value="SuperAgent">Super Agent</SelectItem>
                                        <SelectItem value="Agent">Agent</SelectItem>
                                        <SelectItem value="Marchant">Marchand</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Button variant="outline" size="icon" onClick={fetchAgents}>
                                    <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                                </Button>
                            </div>
                            <div className="rounded-md border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[50px]">Qr</TableHead>
                                            <TableHead>{t('accounts:agents.table.code')}</TableHead>
                                            <TableHead>{t('accounts:agents.table.name')}</TableHead>
                                            <TableHead>{t('common:labels.type')}</TableHead>
                                            <TableHead>{t('accounts:agents.table.balance')}</TableHead>
                                            <TableHead>{t('common:labels.status')}</TableHead>
                                            <TableHead>{t('common:labels.phone')}</TableHead>
                                            <TableHead className="text-right">{t('accounts:agents.table.actions')}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {currentAgents.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={8} className="h-24 text-center">
                                                    {isLoading ? (
                                                        <div className="flex justify-center items-center gap-2">
                                                            <Loader2 className="h-4 w-4 animate-spin" /> {t('common:loading')}...
                                                        </div>
                                                    ) : t('accounts:agents.table.noResults')}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            currentAgents.map((agent) => (
                                                <TableRow key={agent.id}>
                                                    <TableCell>
                                                        <AgentQRDialog agent={agent} />
                                                    </TableCell>
                                                    <TableCell className="font-medium">{agent.code}</TableCell>
                                                    <TableCell>
                                                        <div className="flex flex-col">
                                                            <span className="font-medium">{agent.client?.prenom} {agent.client?.nom}</span>
                                                            <span className="text-xs text-muted-foreground">{agent.client?.email || 'N/A'}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex flex-col">
                                                            <span className="font-medium">
                                                                {agent.type}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex flex-col">
                                                            <span className="font-bold text-gray-900">{agent.solde}</span>
                                                            <span className="text-xs text-slate-500 font-medium">HTG</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${agent.actif ? 'bg-green-100 text-green-800' :
                                                            'bg-red-100 text-red-800'
                                                            }`}>
                                                            {agent.actif ? t('common:status.active') : t('common:status.inactive')}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">{agent.client?.telephone || 'N/A'}</TableCell>
                                                    <TableCell className="text-right">
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" className="h-8 w-8 p-0">
                                                                    <span className="sr-only">{t('accounts:agents.table.actions')}</span>
                                                                    <MoreHorizontal className="h-4 w-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                <DropdownMenuLabel>{t('accounts:agents.table.actions')}</DropdownMenuLabel>
                                                                <DropdownMenuSeparator className="bg-primary-500" />
                                                                <DropdownMenuItem onClick={() => handleViewProfile(agent)}>
                                                                    <Eye className="mr-2 h-4 w-4" />
                                                                    {t('accounts:agents.actions.viewProfile')}
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => handleEdit(agent)}>
                                                                    <Pencil className="mr-2 h-4 w-4" />
                                                                    {t('common:buttons.edit')}
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => handleToggleStatus(agent)}>
                                                                    {agent.actif ? (
                                                                        <>
                                                                            <UserX className="mr-2 h-4 w-4" />
                                                                            {t('accounts:agents.actions.deactivate')}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <UserCheck className="mr-2 h-4 w-4" />
                                                                            {t('accounts:agents.actions.activate')}
                                                                        </>
                                                                    )}
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={async () => {
                                                                    try {
                                                                        await agentsApi.resendPinSetup(agent.userId);
                                                                        toast({ title: t('common:success'), description: t('accounts:agents.actions.resendPin'), type: "success" });
                                                                    } catch (error: any) {
                                                                        const message = error.response?.data?.message || t('common:error');
                                                                        toast({ title: t('common:error'), description: message, type: "error" });
                                                                    }
                                                                }}>
                                                                    <Mail className="mr-2 h-4 w-4" />
                                                                    {t('accounts:agents.actions.resendPin')}
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </TableCell>
                                                </TableRow>
                                            )))}
                                    </TableBody>
                                </Table>
                            </div>
                            <div className="flex items-center justify-end space-x-2 py-4">
                                <div className="flex items-center space-x-2">
                                    <p className="text-sm font-medium">{t('common:labels.rowsPerPage', { defaultValue: 'Lignes par page:' })}</p>
                                    <Select
                                        value={`${itemsPerPage}`}
                                        onValueChange={(value) => {
                                            setItemsPerPage(Number(value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <SelectTrigger className="h-8 w-[70px]">
                                            <SelectValue placeholder={itemsPerPage} />
                                        </SelectTrigger>
                                        <SelectContent side="top">
                                            {[5, 10, 20, 50].map((pageSize) => (
                                                <SelectItem key={pageSize} value={`${pageSize}`}>
                                                    {pageSize}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                                    {filteredAgents.length > 0 ? (
                                        `${indexOfFirstItem + 1}-${Math.min(indexOfLastItem, filteredAgents.length)} ` + t('common:labels.of', { defaultValue: 'sur' }) + ` ${filteredAgents.length}`
                                    ) : (
                                        "0-0 " + t('common:labels.of', { defaultValue: 'sur' }) + " 0"
                                    )}
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        className="hidden h-8 w-8 p-0 lg:flex"
                                        onClick={() => setCurrentPage(1)}
                                        disabled={currentPage === 1}
                                    >
                                        <span className="sr-only">{t('common:buttons.previous')}</span>
                                        <ChevronsLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="h-8 w-8 p-0"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <span className="sr-only">{t('common:buttons.previous')}</span>
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="h-8 w-8 p-0"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                    >
                                        <span className="sr-only">{t('common:buttons.next')}</span>
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="hidden h-8 w-8 p-0 lg:flex"
                                        onClick={() => setCurrentPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                    >
                                        <span className="sr-only">{t('common:buttons.next')}</span>
                                        <ChevronsRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* View Profile Dialog */}
                {selectedAgent && (
                    <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
                        <DialogContent className="sm:max-w-2xl w-[95vw] max-h-[95vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>{t('accounts:agents.actions.viewProfile')}</DialogTitle>
                                <DialogDescription>
                                    {t('accounts:agents.dialogs.profile.description')}
                                </DialogDescription>
                            </DialogHeader>

                            {/* Tabs */}
                            <div className="flex border-b mb-2">
                                <button
                                    className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeAgentTab === 'overview' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                                    onClick={() => setActiveAgentTab('overview')}
                                >
                                    Vue d'ensemble
                                </button>
                                <button
                                    className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeAgentTab === 'clients' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                                    onClick={() => setActiveAgentTab('clients')}
                                >
                                    Kliyan Asiye ({agentClientsTotal})
                                </button>
                            </div>

                            {activeAgentTab === 'clients' && (
                                <div className="space-y-3 min-h-[200px]">
                                    <input
                                        type="text"
                                        placeholder="Chèche kliyan…"
                                        className="w-full border rounded-md px-3 py-1.5 text-sm"
                                        value={agentClientsSearch}
                                        onChange={e => { setAgentClientsSearch(e.target.value); setAgentClientsPage(1); }}
                                    />
                                    {isLoadingAgentClients ? (
                                        <div className="flex justify-center py-6">
                                            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                                        </div>
                                    ) : agentClients.length === 0 ? (
                                        <p className="text-slate-400 text-sm text-center py-6">Pa gen kliyan asiye a ajan sa</p>
                                    ) : (
                                        <div className="divide-y max-h-80 overflow-y-auto">
                                            {agentClients.map(c => (
                                                <div key={c.compte_id} className="py-2.5 flex justify-between items-center">
                                                    <div>
                                                        <p className="text-sm font-semibold text-slate-800">
                                                            {c.client?.prenom} {c.client?.nom}
                                                            {c.client?.surnom && <span className="ml-1 text-xs text-blue-500">@{c.client.surnom}</span>}
                                                        </p>
                                                        <p className="text-xs text-slate-400">{c.compte_code} · {c.type_compte}</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-sm font-bold text-slate-700">{(c.solde_bs || 0).toLocaleString('fr-HT')} HTG</p>
                                                        <p className={`text-xs ${c.statut === 'actif' ? 'text-emerald-500' : 'text-red-400'}`}>{c.statut}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {agentClientsTotal > 20 && (
                                        <div className="flex justify-center gap-2 pt-2">
                                            <Button size="sm" variant="ghost" disabled={agentClientsPage === 1} onClick={() => setAgentClientsPage(p => p - 1)}>← Anvan</Button>
                                            <span className="text-xs text-slate-500 self-center">Paj {agentClientsPage}</span>
                                            <Button size="sm" variant="ghost" disabled={agentClients.length < 20} onClick={() => setAgentClientsPage(p => p + 1)}>Swivan →</Button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeAgentTab === 'overview' && <div className="space-y-6">
                                {/* Header Section */}
                                <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-4 sm:gap-6 pb-2">
                                    <Avatar className="h-16 w-16 sm:h-20 sm:w-20">
                                        <AvatarImage src="" />
                                        <AvatarFallback className="text-xl bg-primary-100 text-primary-700">
                                            {selectedAgent.client?.prenom?.charAt(0)}{selectedAgent.client?.nom?.charAt(0)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="space-y-1">
                                        <h3 className="text-2xl font-semibold tracking-tight">
                                            {selectedAgent.client?.prenom} {selectedAgent.client?.nom}
                                        </h3>
                                        <p className="text-sm text-muted-foreground">
                                            {selectedAgent.type} • {selectedAgent.code}
                                        </p>
                                        <div className="pt-1">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ring-1 ring-inset ${selectedAgent.actif
                                                ? 'bg-green-50 text-green-700 ring-green-600/20'
                                                : 'bg-red-50 text-red-700 ring-red-600/20'
                                                }`}>
                                                {selectedAgent.actif ? t('common:status.active') : t('common:status.inactive')}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <Separator />

                                {/* Form Sections */}
                                <div className="grid gap-6">
                                    {/* Personal Info */}
                                    <div className="space-y-4">
                                        <h4 className="text-sm font-medium leading-none text-primary">{t('accounts:agents.dialogs.profile.personalInfo')}</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>{t('common:labels.email')}</Label>
                                                <Input readOnly value={selectedAgent.client?.email || 'N/A'} className="h-9 bg-transparent border-input/50 shadow-none" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>{t('common:labels.phone')}</Label>
                                                <Input readOnly value={selectedAgent.client?.telephone || 'N/A'} className="h-9 bg-transparent border-input/50 shadow-none" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>{t('common:labels.city')}</Label>
                                                <Input readOnly value={selectedAgent.client?.ville || 'N/A'} className="h-9 bg-transparent border-input/50 shadow-none" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>{t('common:labels.date')}</Label>
                                                <Input readOnly value={new Date(selectedAgent.createdAt).toLocaleDateString()} className="h-9 bg-transparent border-input/50 shadow-none" />
                                            </div>
                                        </div>
                                    </div>

                                    <Separator />

                                    {/* Account Info */}
                                    <div className="space-y-4">
                                        <h4 className="text-sm font-medium leading-none text-primary">{t('accounts:agents.dialogs.profile.accountInfo')}</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>{t('dashboard:balance.available')}</Label>
                                                <div className="relative">
                                                    <Input readOnly value={selectedAgent.solde.toLocaleString()} className="h-9 bg-green-50/30 text-green-700 font-semibold border-green-200/50 shadow-none pr-12" />
                                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-green-700/50 text-sm font-medium">
                                                        HTG
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>{t('dashboard:dashboard.statistics.totalCommission')}</Label>
                                                <div className="relative">
                                                    <Input readOnly value={selectedAgent.commission.toLocaleString()} className="h-9 bg-amber-50/30 text-amber-700 font-semibold border-amber-200/50 shadow-none pr-12" />
                                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-amber-700/50 text-sm font-medium">
                                                        HTG
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <Separator />

                                    {/* Agent Config */}
                                    <div className="space-y-4">
                                        <h4 className="text-sm font-medium leading-none text-primary">Konfigirasyon Ajan</h4>
                                        <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50">
                                            <div className="space-y-0.5">
                                                <p className="text-sm font-medium text-slate-800">Retrè Dirèkteman</p>
                                                <p className="text-xs text-slate-500">
                                                    {agentConfig?.canWithdrawDirect
                                                        ? 'Ajan ka fè retrè san apwobasyon admin'
                                                        : 'Retrè ajan bezwen apwobasyon admin'}
                                                </p>
                                            </div>
                                            <button
                                                onClick={handleToggleWithdrawDirect}
                                                disabled={isUpdatingConfig || !agentConfig}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
                                                    agentConfig?.canWithdrawDirect ? 'bg-green-500' : 'bg-slate-300'
                                                }`}
                                            >
                                                <span
                                                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                                        agentConfig?.canWithdrawDirect ? 'translate-x-6' : 'translate-x-1'
                                                    }`}
                                                />
                                            </button>
                                        </div>
                                        <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50">
                                            <div className="space-y-0.5">
                                                <p className="text-sm font-medium text-slate-800">Aksè Kliyan</p>
                                                <p className="text-xs text-slate-500">
                                                    {(agentConfig?.clientAccessMode ?? 'full') === 'assigned_only'
                                                        ? 'Ajan wè sèlman kliyan asiye ba li'
                                                        : 'Ajan ka wè tout kliyan'}
                                                </p>
                                            </div>
                                            <button
                                                onClick={handleToggleAccessMode}
                                                disabled={isUpdatingConfig || !agentConfig}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
                                                    (agentConfig?.clientAccessMode ?? 'full') === 'assigned_only' ? 'bg-blue-500' : 'bg-slate-300'
                                                }`}
                                            >
                                                <span
                                                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                                        (agentConfig?.clientAccessMode ?? 'full') === 'assigned_only' ? 'translate-x-6' : 'translate-x-1'
                                                    }`}
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>}
                        </DialogContent>
                    </Dialog>
                )}

                {/* Confirmation Dialog */}
                <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>{t('common:status.confirm')}</AlertDialogTitle>
                            <AlertDialogDescription>
                                {t('accounts:agents.actions.confirmMessage', { action: agentToToggle?.actif ? t('accounts:agents.actions.deactivate') : t('accounts:agents.actions.activate') })}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>{t('common:buttons.cancel')}</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={confirmToggleStatus}
                                className={agentToToggle?.actif ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}
                            >
                                {agentToToggle?.actif ? t('accounts:agents.actions.deactivate') : t('accounts:agents.actions.activate')}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Edit Agent Dialog - placeholder for now */}

                {/* Edit Agent Dialog */}
                {selectedAgent && (
                    <EditAgentDialog
                        agent={selectedAgent}
                        open={showEditDialog}
                        onOpenChange={setShowEditDialog}
                        onSuccess={() => {
                            fetchAgents();
                            setShowEditDialog(false);
                        }}
                    />
                )}
            </div>
        </div>
    );
}

function EditAgentDialog({ agent, open, onOpenChange, onSuccess }: { agent: Agent, open: boolean, onOpenChange: (open: boolean) => void, onSuccess: () => void }) {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        nom: agent.client?.nom || "",
        prenom: agent.client?.prenom || "",
        email: agent.client?.email || "",
        telephone: agent.client?.telephone || "",
        ville: agent.client?.ville || "",
        surnom: "", // Client interface missing surnom/sexe/etc in basic view but can be added if backend supports
        adresse: "",
    });
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const { toast } = useToast();

    // Reset form when agent changes
    useEffect(() => {
        setFormData({
            nom: agent.client?.nom || "",
            prenom: agent.client?.prenom || "",
            email: agent.client?.email || "",
            telephone: agent.client?.telephone || "",
            ville: agent.client?.ville || "",
            surnom: "", // Would need full client fetch if these details aren't in list view
            adresse: "",
        });
    }, [agent]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (fieldErrors[name] || fieldErrors.general) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                delete newErrors.general;
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setFieldErrors({});

        try {
            // Mapping to CreateAgentRequest partial
            const updateData: Partial<CreateAgentRequest> = {
                nom: formData.nom,
                prenom: formData.prenom,
                email: formData.email,
                telephone: formData.telephone,
                ville: formData.ville,
                // Add other fields if supported by backend update endpoint
            };

            await agentsApi.update(agent.id, updateData);
            toast({ title: t('common:success'), description: t('accounts:agents.dialogs.edit.success'), type: "success" });
            onSuccess();
        } catch (error: any) {
            const message = error.response?.data?.message || t('accounts:agents.dialogs.edit.error');
            if (message.toLowerCase().includes('email')) {
                setFieldErrors({ email: message });
            } else if (message.toLowerCase().includes('telephone')) {
                setFieldErrors({ telephone: message });
            } else {
                setFieldErrors({ general: message });
                toast({ title: t('common:error'), description: message, type: "error" });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>{t('common:buttons.edit')} {t('dashboard:sidebar.items.agents')} - {agent.code}</DialogTitle>
                    <DialogDescription>
                        {t('accounts:agents.dialogs.edit.description')}
                    </DialogDescription>
                </DialogHeader>

                {fieldErrors.general && (
                    <div className="p-3 mb-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
                        {fieldErrors.general}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="grid gap-4 py-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="edit-prenom">{t('common:labels.firstName')}</Label>
                            <Input id="edit-prenom" name="prenom" value={formData.prenom} onChange={handleChange} required />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="edit-nom">{t('common:labels.lastName')}</Label>
                            <Input id="edit-nom" name="nom" value={formData.nom} onChange={handleChange} required />
                        </div>
                    </div>

                    <div className="grid gap-2">
                        <Label htmlFor="edit-email">{t('common:labels.email')}</Label>
                        <Input
                            id="edit-email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className={fieldErrors.email ? "border-red-500" : ""}
                        />
                        {fieldErrors.email && <p className="text-xs text-red-500">{fieldErrors.email}</p>}
                    </div>

                    <div className="grid gap-2">
                        <Label htmlFor="edit-telephone">{t('common:labels.phone')}</Label>
                        <PhoneInput
                            id="edit-telephone"
                            placeholder="Ex: 509..."
                            value={formData.telephone}
                            onChange={(val: string | undefined) => {
                                setFormData({ ...formData, telephone: val || "" });
                                if (fieldErrors.telephone) {
                                    setFieldErrors(prev => {
                                        const newErrors = { ...prev };
                                        delete newErrors.telephone;
                                        return newErrors;
                                    });
                                }
                            }}
                            defaultCountry="HT"
                            className={fieldErrors.telephone ? "border-red-500" : ""}
                        />
                        {fieldErrors.telephone && <p className="text-xs text-red-500">{fieldErrors.telephone}</p>}
                    </div>

                    <div className="grid gap-2">
                        <Label htmlFor="edit-ville">{t('common:labels.city')}</Label>
                        <Input id="edit-ville" name="ville" value={formData.ville} onChange={handleChange} />
                    </div>

                    <div className="flex justify-end gap-3 mt-4">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            {t('common:buttons.cancel')}
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('common:loading')}...
                                </>
                            ) : (
                                t('common:buttons.save')
                            )}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
